import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { invalidateProjectAccessQueries } from '@/lib/queryInvalidation';

export interface ProjectMemberWithProfile {
  id: string;
  project_id: string;
  user_id: string;
  role: string;
  invited_by: string | null;
  created_at: string;
  profile: {
    id: string;
    nome: string | null;
    email: string;
    avatar_url: string | null;
  } | null;
}

export const useProjectMembers = (projectId: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['project-members', projectId],
    enabled: !!user && !!projectId,
    queryFn: async (): Promise<ProjectMemberWithProfile[]> => {
      const { data, error } = await supabase
        .from('project_members')
        .select('id, project_id, user_id, role, invited_by, created_at, profile:profiles!project_members_user_id_fkey(id, nome, email, avatar_url)')
        .eq('project_id', projectId);

      if (error) throw error;

      return (data ?? []).map((row: any) => ({
        id: row.id,
        project_id: row.project_id,
        user_id: row.user_id,
        role: row.role,
        invited_by: row.invited_by,
        created_at: row.created_at,
        profile: row.profile,
      }));
    },
  });
};

export const useAddProjectMember = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ projectId, userId, role }: { projectId: string; userId: string; role: string }) => {
      const { data, error } = await supabase
        .from('project_members')
        .insert({
          project_id: projectId,
          user_id: userId,
          role,
          invited_by: user!.id,
        })
        .select('id, project_id, user_id, role, invited_by, created_at')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      invalidateProjectAccessQueries(queryClient, vars.projectId);
    },
  });
};

export const useUpdateProjectMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, role, projectId }: { memberId: string; role: string; projectId: string }) => {
      const { data, error } = await supabase
        .from('project_members')
        .update({ role })
        .eq('id', memberId)
        .select()
        .single();
      if (error) throw error;
      if (!data) throw new Error('Permissão não encontrada ou sem autorização para atualizar.');
      return data;
    },
    onSuccess: (_, vars) => {
      invalidateProjectAccessQueries(queryClient, vars.projectId);
    },
  });
};

export const useRemoveProjectMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, projectId }: { memberId: string; projectId: string }) => {
      const { data, error } = await supabase
        .from('project_members')
        .delete()
        .eq('id', memberId)
        .select('id')
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Permissão não encontrada ou sem autorização para remover.');
    },
    onSuccess: (_, vars) => {
      invalidateProjectAccessQueries(queryClient, vars.projectId);
    },
  });
};
